import moment from 'moment';
import { TableHeader } from '../../component/table/table.type';
import ActionButton from '../../component/button/button';
import {
  HarmReportsKebabOption,
  HarmReportsTableHeader,
} from './harmReports.type';
import { OptionWithLoading } from '../userManagement/userManagement.type';
import {
  HarmReport,
  InterventionDecision,
  InterventionStatus,
  SignsOfGamblingHarm,
  UserType,
  GetUsersResponse,
} from '@inplay/models';
import Close from '../../assets/close.png';
import { Dialog } from '../../component/dialog/dialog';
import { camelCase } from 'lodash';
import Loading from '../../assets/loading/loading';
import { Trans } from 'react-i18next';
import TooltipV2 from '../../component/tooltipV2/tooltipV2';
import { isAutoGeneratedReport } from '../../component/harmReportForm/harmReportFormHelper';

export const getHarmReportsTableHeader = (
  isTablet: boolean,
  t: (key: string) => string,
  tooltipParentRef: React.RefObject<HTMLDivElement>,
) => {
  const headers: TableHeader<HarmReport>[] = [
    {
      generateCellContent: (harmReport: HarmReport) =>
        harmReport?.reportRefNo ?? '',
      header: isTablet
        ? HarmReportsTableHeader.NO
        : HarmReportsTableHeader.REPORT_NO,
      sortable: true,
    },
    {
      generateCellContent: (harmReport: HarmReport) =>
        harmReport.createdAt
          ? moment(harmReport.createdAt).format('hh:mm A, DD/MM/YYYY')
          : '-',
      header: HarmReportsTableHeader.CREATED_ON,
    },
    {
      generateCellContent: (harmReport: HarmReport) => (
        <div className="flex items-center gap-2">
          {harmReport.playerProfile?.faceImage && (
            <img
              src={harmReport.playerProfile.faceImage}
              alt=""
              className="w-[40px] h-[40px] rounded-full object-cover"
            />
          )}
          <span>{harmReport.playerProfile.id.slice(0, 5)}</span>
        </div>
      ),
      header: HarmReportsTableHeader.PLAYER_NO,
    },
    {
      generateCellContent: (harmReport: HarmReport) => {
        const { lengthOfPlay, money, behaviour, other } =
          harmReport.signsOfGamblingHarm;

        const translatedLengthOfPlay = getTranslatedTextValues(lengthOfPlay, t);
        const translatedMoney = getTranslatedTextValues(money, t);
        const translatedBehaviour = getTranslatedTextValues(behaviour, t);
        const translatedOther = getTranslatedTextValues(other, t);

        const firstItem: string =
          [
            ...translatedLengthOfPlay,
            ...translatedMoney,
            ...translatedBehaviour,
            ...translatedOther,
          ][0] || '';
        const totalCount: number =
          lengthOfPlay.length + money.length + behaviour.length + other.length;
        const maxLength: number = 20;
        const displayText =
          firstItem.length > maxLength
            ? firstItem.slice(0, maxLength) + '…'
            : firstItem;
        return (
          <div className="relative group inline-block">
            <span className="underline cursor-pointer">
              {displayText} ({totalCount})
            </span>
            {renderHarmObservedDialogContent({
              lengthOfPlay: translatedLengthOfPlay,
              money: translatedMoney,
              behaviour: translatedBehaviour,
              other: translatedOther,
            })}
          </div>
        );
      },

      header: HarmReportsTableHeader.HARM_OBSERVED,
    },
    {
      generateCellContent: (harmReport: HarmReport) => (
        <>
          <span
            className={`w-2 h-2 mr-2 rounded-full inline-block ${getHarmReportsInterventionDecisionDotColor(
              harmReport.intervention.decision,
            )}`}
          />
          {harmReport.intervention.decision}
        </>
      ),
      header: (
        <span className="flex items-center gap-2">
          {HarmReportsTableHeader.INTERVENTION_DECISION}
          <TooltipV2
            relativeContainerRef={tooltipParentRef}
            child={
              <div ref={tooltipParentRef} className="tooltipButton">
                i
              </div>
            }
            tooltipContent={
              <div className="tooltipContent">
                <Trans i18nKey="harmReports.info" />
              </div>
            }
          />
        </span>
      ),
    },
    {
      generateCellContent: (harmReport: HarmReport) => (
        <>
          <span
            className={`w-2 h-2 mr-2 rounded-full inline-block ${getHarmReportsInterventionStatusDotColor(
              harmReport.intervention.status,
            )}`}
          />
          {harmReport.intervention.status}
        </>
      ),
      header: HarmReportsTableHeader.INTERVENTION_STATUS,
    },
    {
      generateCellContent: (harmReport: HarmReport) => {
        if (isAutoGeneratedReport(harmReport)) {
          return t('reportDetails.autogeneratedReport');
        }
        return harmReport.createdBy?.firstName
          ? `${harmReport.createdBy.firstName} ${harmReport.createdBy.lastName}`
          : '-';
      },
      header: HarmReportsTableHeader.CREATED_BY,
    },
    {
      generateCellContent: (harmReport: HarmReport) => {
        if (isAutoGeneratedReport(harmReport)) {
          return 'N/A';
        }
        return harmReport.reviewedBy?.firstName
          ? `${harmReport.reviewedBy.firstName} ${harmReport.reviewedBy.lastName}`
          : '-';
      },
      header: HarmReportsTableHeader.REVIEWED_BY,
    },
  ];

  return isTablet ? headers.slice(0, headers.length - 1) : headers;
};

export const getHarmReportsKebabOptions = <
  T extends { reviewedBy?: any; harmInstanceCountForPlayer?: string },
  Kebabaction extends string = string,
>(
  harmReport: T,
  loadingMenu: { rowIndex: number; action: Kebabaction } | null,
  rowIndex: number,
): OptionWithLoading<HarmReportsKebabOption>[] => {
  const isReviewed = Boolean(harmReport.reviewedBy);
  const is1stInstance = harmReport.harmInstanceCountForPlayer === '1st';

  const excludedOptions: HarmReportsKebabOption[] = [];

  // 1st instance and After review: no Review & Sign
  if (is1stInstance || isReviewed) {
    excludedOptions.push(HarmReportsKebabOption.REVIEW_AND_SIGN);
  }

  const options: OptionWithLoading<HarmReportsKebabOption>[] = Object.values(
    HarmReportsKebabOption,
  )
    .filter((option) => !excludedOptions.includes(option))
    .map((option) => ({
      label: option,
      value: camelCase(option),
      loading:
        loadingMenu?.rowIndex === rowIndex &&
        loadingMenu?.action === camelCase(option),
    }));

  return options;
};

export const getHarmReportsInterventionStatusDotColor = (
  status: InterventionStatus,
) => {
  switch (status) {
    case InterventionStatus.PENDING:
      return 'bg-[#FFB103]';
    case InterventionStatus.COMPLETE:
      return 'bg-[#44D185]';
    case InterventionStatus.MISSED:
      return 'bg-[#F86060]';
    case InterventionStatus.NOT_APPLICABLE:
      return 'bg-[#D8D9E5]';
    default:
      return '';
  }
};

export const getHarmReportsInterventionDecisionDotColor = (
  decision: InterventionDecision,
) => {
  switch (decision) {
    case InterventionDecision.REQUIRED:
      return 'bg-[#8782FA]';
    case InterventionDecision.NOT_REQUIRED:
      return 'bg-[#D8D9E5]';
    case InterventionDecision.PENDING:
      return 'bg-[#FFB103]';
    default:
      return '';
  }
};

export const renderHarmObservedDialogContent = (
  signsOfGamblingHarm: SignsOfGamblingHarm,
) => {
  const fields: { label: string; items: string[] }[] = [
    {
      label: 'Length of Play',
      items: signsOfGamblingHarm.lengthOfPlay,
    },
    { label: 'Money', items: signsOfGamblingHarm.money },
    { label: 'Behaviour', items: signsOfGamblingHarm.behaviour },
    { label: 'Other', items: signsOfGamblingHarm.other },
  ];

  return (
    <div className="hidden group-hover:block absolute z-[9999]">
      <Dialog>
        {fields.map(({ label, items }) => (
          <div key={label} className="mb-3">
            <strong>{label}</strong>
            {items.length > 0 ? (
              <ul className="list-disc ml-5 mt-1">
                {items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <div className="ml-5 mt-1 text-gray-500">-</div>
            )}
          </div>
        ))}
      </Dialog>
    </div>
  );
};

export const getHarmReportTitle = (
  reportRefNo: string | undefined,
  isReviewAndSignMode: boolean,
  t: (key: string) => string,
) => {
  return reportRefNo
    ? isReviewAndSignMode
      ? `${t('createHarmReport.title.review')} ${reportRefNo ? `- #${reportRefNo}` : ''}`
      : `${t('createHarmReport.title.edit')} ${reportRefNo ? `- #${reportRefNo}` : ''}`
    : `${t('createHarmReport.title.new')}`;
};

export const getHarmReportData = (
  harmReportForCreate: HarmReport | null,
  mapHarmReportToHarmReportData: (report: any) => any,
) => {
  return harmReportForCreate
    ? mapHarmReportToHarmReportData(harmReportForCreate)
    : {};
};

export const getTranslatedTextValues = (
  textValues: string[],
  t: (key: string) => string,
) => {
  return textValues.map(t);
};

const hasIdProperty = (obj: any): obj is { _id: string } => {
  return obj && typeof obj._id === 'string';
};

export const validateReportsForDeletion = (
  reports: HarmReport[],
  userDetail?: GetUsersResponse,
): { valid: HarmReport[]; invalid: string[] } => {
  const currentDate: Date = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const valid: HarmReport[] = [];
  const invalid: string[] = [];

  reports.forEach((report) => {
    if (report.reviewedBy) {
      invalid.push('Reviewed reports cannot be deleted');
      return;
    }

    const reportDate: Date = new Date(report.createdAt);
    reportDate.setHours(0, 0, 0, 0);
    if (reportDate.getTime() !== currentDate.getTime()) {
      invalid.push('Only current date reports can be deleted');
      return;
    }

    if (userDetail?.type === UserType.VENUE_STAFF) {
      if (
        !hasIdProperty(report.identifiedBy) ||
        report.identifiedBy._id !== userDetail.id
      ) {
        invalid.push('Staff can only delete reports they created');
        return;
      }
    }

    valid.push(report);
  });

  return { valid, invalid };
};

/**
 * Renders a group of action buttons (Download, Delete) along with a summary of selected items.
 *
 * @param selectedCount - Number of selected items to show in the summary.
 * @param onClose - Callback triggered when the close icon is clicked.
 * @param onDelete - Callback triggered when the "Delete" button is clicked.
 * @param onDownload - Callback triggered when the "Download" button is clicked.
 *
 * @returns JSX.Element containing the action buttons and selection summary.
 */
export const renderActionButtons = (
  selectedCount: number,
  reportLabel: string,
  onClose?: () => void,
  onDelete?: () => void,
  onDownload?: () => void,
  showDownloadLoading?: boolean,
) => {
  return (
    <div className="flex items-center flex-wrap gap-2">
      {
        <ActionButton
          className="px-8 py-2 ml-2 bg-white hover:bg-white hover:text-[#1A1A1A] text-[#1A1A1A] border border-black rounded-full text-sm"
          onClick={onDownload}
        >
          {showDownloadLoading ? (
            <Loading height={20} width={20} />
          ) : (
            <Trans i18nKey="harmReports.actionButton.download" />
          )}
        </ActionButton>
      }

      {
        <ActionButton
          className="px-8 py-2 ml-2 bg-white hover:bg-white hover:text-[#E50000] text-[#E50000] border border-black rounded-full text-sm"
          onClick={onDelete}
        >
          <Trans i18nKey="harmReports.actionButton.delete" />
        </ActionButton>
      }

      <span className="flex items-center text-[12px] text-[#092344] lowercase">
        <Trans
          i18nKey="harmReports.selectedHarmReport"
          values={{
            count: selectedCount,
            report: reportLabel,
          }}
        />{' '}
        <img
          onClick={onClose}
          src={Close}
          alt="Close"
          className="w-3 h-3 cursor-pointer ml-3"
        />
      </span>
    </div>
  );
};

export const formatSelectedReportRefs = (reports: HarmReport[]): string => {
  return reports
    .map((report) => report.reportRefNo)
    .filter(Boolean)
    .join(', ');
};

export const getSettingsMenuForHarmReports = () => {
  return [
    { label: 'harmReports.settingOption.download' },
    { label: 'harmReports.settingOption.deletedReports' },
  ];
};
